import {
  CreateWorkItemParams,
  GetAlarmConfigurationsParams,
  GetDelayConfigurationsParams,
  GetFaultAlarmConfigurationsParams,
  GetHealthCheckConfigurationsParams,
  GetJourneyLinesParams,
  GetJourneysParams,
  GetMinorDelayConfigurationsParams,
  GetProjectedDelayConfigurationsParams,
  GetReasonCodesParams,
  GetStatusItemsParams,
  GetWorkItemFieldRequirementsParams,
  GetWorkItemsByStatusParams,
  GetWorkItemsParams,
  UpdateWorkItemParams,
} from '@/api/types';
import { washingRoutes } from '@/api/routes/washingRoutes';
import { buildPathWithParams } from '@/api';

interface RouteDefinition {
  method: 'get' | 'post' | 'put' | 'delete' | 'patch';
  path: (...params: any[]) => string;
}

export const apiRoutes = {
  // WorkItems
  getWorkItems: {
    method: 'get',
    path: (params: GetWorkItemsParams) =>
      buildPathWithParams('/WorkItems', {
        Status: params.status,
        Type: params.type,
        ContractId: params.contractId,
        From: params.from,
        To: params.to,
      }),
  },
  getWorkItemById: {
    method: 'get',
    path: (id: number | string) => `/WorkItems/${id}`,
  },
  createWorkItem: {
    method: 'post',
    path: (params: CreateWorkItemParams) =>
      buildPathWithParams(`/WorkItems`, {
        SendToPta: params.sendToPta,
      }),
  },
  deleteWorkItemById: {
    method: 'delete',
    path: (id: number | string) => `/WorkItems/${id}`,
  },
  restoreWorkItemById: {
    method: 'patch',
    path: (id: number | string) => `/WorkItems/${id}/restore`,
  },
  updateWorkItemById: {
    method: 'put',
    path: (id: number | string, params: UpdateWorkItemParams) =>
      buildPathWithParams(`/WorkItems/${id}`, {
        SendToPta: params.sendToPta,
      }),
  },
  updateWorkItemReasonById: {
    method: 'put',
    path: (id: number | string) => `/WorkItems/${id}/reason`,
  },
  getDeletedWorkItems: {
    method: 'get',
    path: (params: GetWorkItemsByStatusParams) =>
      buildPathWithParams('/WorkItems/deleted', {
        Type: params.type,
        ContractId: params.contractId,
        From: params.from,
        To: params.to,
      }),
  },
  getDoneWorkItems: {
    method: 'get',
    path: (params: GetWorkItemsByStatusParams) =>
      buildPathWithParams('/WorkItems/done', {
        Type: params.type,
        ContractId: params.contractId,
        From: params.from,
        To: params.to,
      }),
  },
  getProjectedWorkItems: {
    method: 'get',
    path: (params: GetWorkItemsByStatusParams) =>
      buildPathWithParams('/WorkItems/projected', {
        Type: params.type,
        ContractId: params.contractId,
        From: params.from,
        To: params.to,
      }),
  },
  getQueuedWorkItems: {
    method: 'get',
    path: (params: GetWorkItemsByStatusParams) =>
      buildPathWithParams('/WorkItems/queued', {
        Type: params.type,
        ContractId: params.contractId,
        From: params.from,
        To: params.to,
      }),
  },
  getRelatedWorkItemsByWorkItemId: {
    method: 'get',
    path: (id: number | string) => `/WorkItems/${id}/related`,
  },
  getPtaReportDetailsByWorkItemId: {
    method: 'get',
    path: (id: number | string) => `/WorkItems/${id}/pta-report-details`,
  },
  getJourneyStopsByWorkItemId: {
    method: 'get',
    path: (id: number | string) => `/WorkItems/${id}/journey-stops`,
  },
  getStopsByWorkItemId: {
    method: 'get',
    path: (id: number | string) => `/WorkItems/${id}/stops`,
  },
  getWorkItemChanges: {
    method: 'get',
    path: (id: number | string) => `/WorkItems/${id}/changes`,
  },
  getWorkItemFieldRequirements: {
    method: 'get',
    path: (params: GetWorkItemFieldRequirementsParams) =>
      buildPathWithParams('/WorkItems/field-requirements', {
        contractId: params.contractId,
        type: params.type,
        status: params.status,
        creationType: params.creationType,
        reasonCode: params.reasonCode,
      }),
  },
  batchCreateWorkItems: {
    method: 'post',
    path: () => '/WorkItems/batch',
  },
  batchDeleteWorkItems: {
    method: 'delete',
    path: () => '/WorkItems/batch',
  },
  batchUpdateWorkItems: {
    method: 'put',
    path: () => '/WorkItems/batch',
  },
  batchRestoreWorkItems: {
    method: 'patch',
    path: () => '/WorkItems/batch/restore',
  },
  // Reason Codes
  getReasonCodes: {
    method: 'get',
    path: (params: GetReasonCodesParams) =>
      buildPathWithParams('/ReasonCodes', {
        workItemType: params.workItemType,
      }),
  },
  // Journeys
  getJourneys: {
    method: 'get',
    path: (params: GetJourneysParams) =>
      buildPathWithParams('/Journeys', {
        ContractId: params?.contractIds,
        From: params?.from,
        To: params?.to,
      }),
  },
  getJourneyById: {
    method: 'get',
    path: (id: number | string) => `/Journeys/${id}`,
  },
  getJourneyStops: {
    method: 'get',
    path: (id: number | string) => `/Journeys/${id}/stops`,
  },
  getJourneyLines: {
    method: 'get',
    path: (params: GetJourneyLinesParams) =>
      buildPathWithParams(`/Journeys/lines`, {
        ContractId: params.contractId,
      }),
  },
  // Current User
  getCurrentUser: {
    method: 'get',
    path: () => `/User`,
  },
  getCurrentUserPermissions: {
    method: 'get',
    path: () => `/User/permissions`,
  },
  getCurrentUserSettings: {
    method: 'get',
    path: () => `/User/settings`,
  },
  updateCurrentUserSettings: {
    method: 'put',
    path: () => `/User/settings`,
  },
  // Users
  getUsers: {
    method: 'get',
    path: () => `/Users`,
  },
  getUserById: {
    method: 'get',
    path: (id: number) => `/Users/${id}`,
  },
  getUsersAndPermissions: {
    method: 'get',
    path: () => `/Users/permissions`,
  },
  // Applications a grant can be given on, each with the roles allowed inside
  // it. The User Management editor renders its rows and selector options from
  // this response rather than from the local enums.
  getApplicationRoles: {
    method: 'get',
    path: () => `/Users/applications`,
  },
  updateUserPermissionsById: {
    method: 'put',
    path: (id: number | string) => `/Users/${id}/permissions`,
  },
  // Alarm Configurations
  getAlarmConfigurations: {
    method: 'get',
    path: (params: GetAlarmConfigurationsParams) =>
      buildPathWithParams(`/Configurations/Alarms`, {
        ContractId: params.contractId,
      }),
  },
  createAlarmConfiguration: {
    method: 'post',
    path: () => `/Configurations/Alarms`,
  },
  deleteAlarmConfigurationById: {
    method: 'delete',
    path: (id: number) => `/Configurations/Alarms/${id}`,
  },
  updateAlarmConfigurationById: {
    method: 'put',
    path: (id: number) => `/Configurations/Alarms/${id}`,
  },
  getAlarmConfigurationById: {
    method: 'get',
    path: (id: number) => `/Configurations/Alarms/${id}`,
  },
  // Delay Configurations
  getDelayConfigurations: {
    method: 'get',
    path: (params: GetDelayConfigurationsParams) =>
      buildPathWithParams(`/Configurations/Delays`, {
        ContractId: params.contractId,
      }),
  },
  createDelayConfiguration: {
    method: 'post',
    path: () => `/Configurations/Delays`,
  },
  deleteDelayConfigurationById: {
    method: 'delete',
    path: (id: number) => `/Configurations/Delays/${id}`,
  },
  updateDelayConfigurationById: {
    method: 'put',
    path: (id: number) => `/Configurations/Delays/${id}`,
  },
  getDelayConfigurationById: {
    method: 'get',
    path: (id: number) => `/Configurations/Delays/${id}`,
  },
  // Health Check Configurations
  getHealthCheckConfigurations: {
    method: 'get',
    path: (params: GetHealthCheckConfigurationsParams) =>
      buildPathWithParams(`/Configurations/HealthChecks`, {
        ContractId: params.contractId,
      }),
  },
  updateHealthCheckConfigurationById: {
    method: 'put',
    path: (id: number | string) => `/Configurations/HealthChecks/${id}`,
  },
  updateTemperatureCheckpoints: {
    method: 'put',
    path: () => `/Configurations/HealthChecks/TemperatureCheckpoints`,
  },
  // Fault Alarm Configurations
  getFaultAlarmConfigurations: {
    method: 'get',
    path: (params: GetFaultAlarmConfigurationsParams) =>
      buildPathWithParams(`/Configurations/FaultAlarms`, {
        ContractId: params.contractId,
      }),
  },
  createFaultAlarmConfiguration: {
    method: 'post',
    path: () => `/Configurations/FaultAlarms`,
  },
  updateFaultAlarmConfigurationById: {
    method: 'put',
    path: (id: number | string) => `/Configurations/FaultAlarms/${id}`,
  },
  deleteFaultAlarmConfigurationById: {
    method: 'delete',
    path: (id: number | string) => `/Configurations/FaultAlarms/${id}`,
  },
  // Minor Delay Configurations
  getMinorDelayConfigurations: {
    method: 'get',
    path: (params: GetMinorDelayConfigurationsParams) =>
      buildPathWithParams(`/Configurations/MinorDelays`, {
        ContractId: params.contractId,
      }),
  },
  createMinorDelayConfiguration: {
    method: 'post',
    path: () => `/Configurations/MinorDelays`,
  },
  deleteMinorDelayConfigurationById: {
    method: 'delete',
    path: (id: number) => `/Configurations/MinorDelays/${id}`,
  },
  updateMinorDelayConfigurationById: {
    method: 'put',
    path: (id: number) => `/Configurations/MinorDelays/${id}`,
  },
  getMinorDelayConfigurationById: {
    method: 'get',
    path: (id: number) => `/Configurations/MinorDelays/${id}`,
  },
  // Projected Delay Configurations
  getProjectedDelayConfigurations: {
    method: 'get',
    path: (params: GetProjectedDelayConfigurationsParams) =>
      buildPathWithParams(`/Configurations/ProjectedDelays`, {
        ContractId: params.contractId,
      }),
  },
  createProjectedDelayConfiguration: {
    method: 'post',
    path: () => `/Configurations/ProjectedDelays`,
  },
  deleteProjectedDelayConfigurationById: {
    method: 'delete',
    path: (id: number) => `/Configurations/ProjectedDelays/${id}`,
  },
  updateProjectedDelayConfigurationById: {
    method: 'put',
    path: (id: number) => `/Configurations/ProjectedDelays/${id}`,
  },
  getProjectedDelayConfigurationById: {
    method: 'get',
    path: (id: number) => `/Configurations/ProjectedDelays/${id}`,
  },
  // Bus Is Late Configurations
  getBusIsLateConfigurations: {
    method: 'get',
    path: () => `/Configurations/BusIsLate`,
  },
  createBusIsLateConfiguration: {
    method: 'post',
    path: () => `/Configurations/BusIsLate`,
  },
  deleteBusIsLateConfigurationById: {
    method: 'delete',
    path: (id: number) => `/Configurations/BusIsLate/${id}`,
  },
  updateBusIsLateConfigurationById: {
    method: 'put',
    path: (id: number) => `/Configurations/BusIsLate/${id}`,
  },
  // Washing (cleaning list, next departures, monitored depots, rule configs)
  ...washingRoutes,
  // Depots
  getDepots: {
    method: 'get',
    path: () => `/Depots`,
  },
  // Status Items
  getStatusItems: {
    method: 'get',
    path: (params: GetStatusItemsParams) =>
      buildPathWithParams(`/StatusItems`, {
        ContractId: params.contractId,
        DepotId: params.depotId,
        IncludeHealthy: params.includeHealthy,
        IncludeMuted: params.includeMuted,
        Responsibility: params.responsibility,
      }),
  },
  createStatusItemMuting: {
    method: 'post',
    path: () => `/StatusItemsMuting`,
  },
  deleteStatusItemMutingById: {
    method: 'delete',
    path: (id: number) => `/StatusItemsMuting/${id}`,
  },
  // Reason Comment Configurations
  getReasonComments: {
    method: 'get',
    path: () => `/Configurations/ReasonComments`,
  },
  updateReasonCommentDefault: {
    method: 'put',
    path: (reasonCode: string) =>
      `/Configurations/ReasonComments/${reasonCode}`,
  },
  upsertReasonCommentOverride: {
    method: 'put',
    path: (reasonCode: string, workItemType: number) =>
      `/Configurations/ReasonComments/${reasonCode}/overrides/${workItemType}`,
  },
  deleteReasonCommentOverride: {
    method: 'delete',
    path: (reasonCode: string, workItemType: number) =>
      `/Configurations/ReasonComments/${reasonCode}/overrides/${workItemType}`,
  },
} as const satisfies Record<string, RouteDefinition>;
